import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Skeleton } from './ui/skeleton';
import { Badge } from './ui/badge';
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Coins,
  CreditCard,
  Hourglass,
  Receipt,
  Table as TableIcon,
  XCircle,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { cn } from '../lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BUCKET_ICON = {
  POS_ROUNDING: Coins,
  ZERO_TOTAL: XCircle,
  AGGREGATOR_LAG: Clock,
  DATA_SYNC_DELAY: Hourglass,
  OPEN_TABLE: TableIcon,
  POS_GAP: Receipt,
  PAYMENT_FAILURE: CreditCard,
  EDGE_CASE: AlertTriangle,
};

const COLOR_CLASS = {
  muted: 'bg-muted/30 text-muted-foreground border-border/40',
  emerald: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30',
  sky: 'bg-sky-500/10 text-sky-400 border-sky-500/30',
  amber: 'bg-amber-500/10 text-amber-400 border-amber-500/30',
  orange: 'bg-orange-500/10 text-orange-400 border-orange-500/30',
  rose: 'bg-rose-500/10 text-rose-400 border-rose-500/30',
  violet: 'bg-violet-500/10 text-violet-400 border-violet-500/30',
};

const SEVERITY_HEAD = {
  critical: { icon: XCircle, color: 'rose', label: 'CRITICAL' },
  needs_action: { icon: AlertTriangle, color: 'orange', label: 'NEEDS ACTION' },
  in_progress: { icon: Clock, color: 'amber', label: 'IN PROGRESS' },
  review: { icon: AlertTriangle, color: 'violet', label: 'REVIEW' },
  expected: { icon: CheckCircle2, color: 'emerald', label: 'EXPECTED' },
  noise: { icon: CheckCircle2, color: 'muted', label: 'CLEAN' },
};

function fmt(amt) {
  return `₹${(Number(amt) || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
}

/**
 * UnpaidOrdersCard — Phase 5 operational card.
 *
 * Surfaces the real risk number (`ar_exposure`) and a breakdown of
 * unpaid orders into operational buckets. Click-to-expand each bucket
 * to see the action and sample orders.
 */
export default function UnpaidOrdersCard({ dateFrom, dateTo }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openBucket, setOpenBucket] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const token =
          sessionStorage.getItem('token') ||
          sessionStorage.getItem('access_token') ||
          localStorage.getItem('token') ||
          localStorage.getItem('access_token');
        const params = new URLSearchParams();
        if (dateFrom) params.set('date_from', dateFrom);
        if (dateTo) params.set('date_to', dateTo);
        const url = `${BACKEND_URL}/api/accounts/unpaid-analysis${params.toString() ? '?' + params : ''}`;
        const res = await fetch(url, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) {
          if (!cancelled) {
            setError(`http_${res.status}`);
            setLoading(false);
          }
          return;
        }
        const json = await res.json();
        if (!cancelled) {
          setData(json);
          setLoading(false);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e.message);
          setLoading(false);
        }
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [dateFrom, dateTo]);

  if (loading) {
    return (
      <Card className="bg-card/40 border-border/40" data-testid="unpaid-orders-card-loading">
        <CardHeader>
          <CardTitle className="text-sm">Unpaid Orders</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-12 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card className="bg-card/40 border-border/40" data-testid="unpaid-orders-card-error">
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-400" />
            Unpaid Orders — unavailable
          </CardTitle>
        </CardHeader>
        <CardContent className="text-xs text-muted-foreground">
          {error || 'no_data'}
        </CardContent>
      </Card>
    );
  }

  const headSev = SEVERITY_HEAD[data.headline_severity] || SEVERITY_HEAD.noise;
  const HeadIcon = headSev.icon;
  const visibleBuckets = Object.entries(data.buckets)
    .filter(([, b]) => b.count > 0)
    .sort((a, b) => b[1].amount - a[1].amount);

  return (
    <Card className="bg-card/40 border-border/40" data-testid="unpaid-orders-card">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-sm">Unpaid Orders Breakdown</CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              {data.total_orders} orders · {data.window?.date_from} → {data.window?.date_to}
            </p>
          </div>
          <Badge
            className={cn(
              'gap-1.5 border',
              COLOR_CLASS[headSev.color] || COLOR_CLASS.muted
            )}
            data-testid={`unpaid-headline-${data.headline_severity}`}
          >
            <HeadIcon className="w-3 h-3" />
            {headSev.label}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* AR exposure — the actual risk number */}
        <div className="rounded-lg border border-border/40 bg-muted/20 p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                AR Exposure (real risk)
              </div>
              <div className="text-2xl font-bold mt-1" data-testid="unpaid-ar-exposure">
                {fmt(data.ar_exposure)}
              </div>
            </div>
            <div className="text-right text-xs text-muted-foreground">
              <div>Outstanding total: <span className="font-mono text-foreground">{fmt(data.total_amount_outstanding)}</span></div>
              <div>Noise excluded: <span className="font-mono text-foreground">{fmt(data.noise_amount)}</span></div>
              <div>Aggregator (expected): <span className="font-mono text-foreground">{fmt(data.buckets?.AGGREGATOR_LAG?.amount || 0)}</span></div>
            </div>
          </div>
        </div>

        {/* Bucket list */}
        <div className="space-y-2">
          {visibleBuckets.length === 0 && (
            <div className="text-xs text-muted-foreground py-4 text-center">
              No unpaid orders in window. Books are clean.
            </div>
          )}
          {visibleBuckets.map(([name, b]) => {
            const Icon = BUCKET_ICON[name] || AlertTriangle;
            const color = COLOR_CLASS[b.color] || COLOR_CLASS.muted;
            const isOpen = openBucket === name;
            return (
              <div
                key={name}
                className="rounded-md border border-border/40 overflow-hidden"
                data-testid={`unpaid-bucket-${name.toLowerCase()}`}
              >
                <button
                  onClick={() => setOpenBucket(isOpen ? null : name)}
                  className={cn(
                    'w-full px-3 py-2.5 flex items-center justify-between gap-3 text-left transition-colors hover:bg-muted/30 border-l-2',
                    color
                  )}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <Icon className="w-4 h-4 flex-shrink-0" />
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{b.label}</div>
                      <div className="text-[10px] uppercase tracking-wider opacity-70">
                        {b.severity}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <div className="text-right">
                      <div className="text-sm font-mono font-semibold">{fmt(b.amount)}</div>
                      <div className="text-[10px] opacity-70">{b.count} order{b.count === 1 ? '' : 's'}</div>
                    </div>
                    {isOpen ? <ChevronDown className="w-4 h-4 opacity-50" /> : <ChevronRight className="w-4 h-4 opacity-50" />}
                  </div>
                </button>
                {isOpen && (
                  <div className="px-4 py-3 bg-background/50 border-t border-border/40 space-y-3">
                    <div className="text-xs text-muted-foreground italic">
                      {b.action}
                    </div>
                    {(b.sample_orders || []).length > 0 && (
                      <div className="space-y-1">
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                          Sample orders
                        </div>
                        <div className="space-y-1">
                          {b.sample_orders.map((o) => (
                            <div
                              key={o.sc_transaction_id}
                              className="text-[11px] flex items-center justify-between gap-2 py-1 px-2 rounded bg-muted/40 font-mono"
                            >
                              <span className="text-muted-foreground">
                                {o.sc_transaction_id} · {o.business_date}
                              </span>
                              <span className="text-muted-foreground">
                                {o.partner_id || o.channel || o.order_source || '—'}
                              </span>
                              <span className="font-semibold">{fmt(o.unpaid)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="pt-1 text-[10px] text-muted-foreground text-center">
          Phase 5 · classified into 7 operational buckets
        </div>
      </CardContent>
    </Card>
  );
}
